import { AlertTriangle } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { StatePanel } from '@/components/ui/StatePanel';
import { usePermission } from '@/hooks/usePermission';

import type { NotificationRule } from '../types';
import { useNotificationRules, useStageDeadlines } from '../use-notification-settings';

import { ActiveEscalations } from './ActiveEscalations';
import { EscalationCard } from './EscalationCard';
import { SettingsSkeleton } from './NotificationsSkeleton';
import { RuleEditModal } from './RuleEditModal';
import { RulesCard } from './RulesCard';
import { StageDeadlinesCard } from './StageDeadlinesCard';
import { errorAdvice } from '@/lib/error-advice';

/** Pestaña Configuración (so=370, diseño del mockup): reglas a la izquierda; escalamiento,
 *  escalados activos y plazos por etapa a la derecha. Todo de la empresa activa. */
export function NotificationSettings({ companyId }: { companyId: string }): ReactElement {
  const scope = { companyId, enabled: true };
  const rules = useNotificationRules(scope);
  const deadlines = useStageDeadlines(scope);
  const canReadPanel = usePermission('quality:master-list:read');
  const [editing, setEditing] = useState<NotificationRule | null>(null);

  if (rules.isLoading || deadlines.isLoading) {
    return <SettingsSkeleton />;
  }
  if (rules.isError || deadlines.isError || !rules.data || !deadlines.data) {
    return (
      <StatePanel
        icon={<AlertTriangle />}
        title="No se pudo cargar la configuración"
        tone="danger"
        role="alert"
        onRetry={() => {
          void rules.refetch();
          void deadlines.refetch();
        }}
        retrying={rules.isRefetching || deadlines.isRefetching}
      >
        {errorAdvice(rules.error ?? deadlines.error)}
      </StatePanel>
    );
  }

  const list = rules.data;
  const overdue = list.find((r) => r.code === 'stage.overdue');
  const escalation = list.find((r) => r.code === 'escalation');

  return (
    <>
      <div className="g-2">
        <RulesCard rules={list} onEdit={setEditing} />
        <div className="stack">
          {overdue && escalation && (
            <EscalationCard
              overdue={overdue}
              escalation={escalation}
              onEdit={() => setEditing(overdue)}
            />
          )}
          <ActiveEscalations companyId={companyId} enabled={canReadPanel} />
          <StageDeadlinesCard key={companyId} deadlines={deadlines.data} />
        </div>
      </div>
      <RuleEditModal
        key={editing?.code ?? 'none'}
        rule={editing}
        onClose={() => setEditing(null)}
      />
    </>
  );
}
